import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  Grid,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../Config";
import { toast } from "react-toastify";
import { getAuthToken } from "../../Redux/Service/FetchBaseQuery";
import StyledDashboardLayout from "../style";
import * as Yup from "yup";

const EditTechStack = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [techStackData, setTechStackData] = useState(null);

  // Technology types for the dropdown
  const technologyTypes = [
    { value: "Frontend", label: "Frontend" },
    { value: "Backend", label: "Backend" },
    { value: "Database", label: "Database" },
    { value: "DevOps", label: "DevOps" },
    { value: "Mobile", label: "Mobile" },
    { value: "CMS", label: "CMS" },
    { value: "UI/UX", label: "UI/UX" },
    { value: "SEO", label: "SEO" },
  ];

  // Validation schema
  const validationSchema = Yup.object().shape({
    type: Yup.string().required("Technology type is required"),
    technology: Yup.string()
      .trim()
      .required("Technology name is required")
      .matches(/^[^0-9]*$/, "Numbers are not allowed")
      .min(2, "Technology name must be at least 2 characters")
      .max(100, "Technology name must not exceed 100 characters"),
    status: Yup.boolean().required("Status is required"),
  });

  // Fetch tech stack data
  useEffect(() => {
    const fetchTechStackData = async () => {
      try {
        setFetchLoading(true);
        const token = getAuthToken();
        const response = await axios.get(
          `${API_URL}technology-stack/list-technology/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.data) {
          setTechStackData(response.data.data);
        } else {
          toast.error("Failed to fetch tech stack data");
          navigate("/tech-stack/techstacklist");
        }
      } catch (error) {
        console.error("Error fetching tech stack:", error);
        toast.error("Failed to fetch tech stack data");
        navigate("/tech-stack/techstacklist");
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchTechStackData();
    } else {
      navigate("/tech-stack/techstacklist");
    }
  }, [id, navigate]);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      setLoading(true);
      Object.entries(values).forEach(([key, val]) => typeof val === 'string' ? values[key] = val.trim() : val);
      console.log(values, "opo")
      const token = getAuthToken();

      const response = await axios.patch(
        `${API_URL}technology-stack/update/${id}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Tech Stack updated successfully:", response);
      toast.success(response?.data?.message || "Tech Stack updated successfully!");

      // Navigate back to list
      navigate("/tech-stack/techstacklist");
    } catch (error) {
      console.error("Error updating tech stack:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update tech stack"
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Show loading spinner while fetching data
  if (fetchLoading) {
    return (
      <StyledDashboardLayout>
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between mb-2">
                <div className="pageTitle">
                  <h1 className="m-0">Edit Technology Stack</h1>
                </div>
                <div className="btnWrap">
                  <Link to="/tech-stack/techstacklist" className="btn addBtn">
                    <ArrowBackIcon /> Back
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="card customCard p-3 m-3">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading tech stack data...
              </Typography>
            </Box>
          </div>
        </div>
      </StyledDashboardLayout>
    );
  }

  // Initial form values from fetched data
  const initialValues = {
    type: techStackData?.type || "",
    technology: techStackData?.technology || "",
    status: techStackData?.status !== undefined ? techStackData.status : true,
  };

  return (
    <StyledDashboardLayout>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="d-flex justify-content-between mb-2">
              <div className="pageTitle">
                <h1 className="m-0">Edit Technology Stack</h1>
              </div>
              <div className="btnWrap">
                <Link to="/tech-stack/techstacklist" className="btn addBtn">
                  <ArrowBackIcon /> Back
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="card customCard p-3 m-3">
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#333",
                mb: 3,
                display: "flex",
                alignItems: "center",
              }}
            >
              Update Technology Stack Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                setFieldValue,
                touched,
                errors,
                isSubmitting,
                handleBlur,
                handleChange,
              }) => (
                <Form>
                  <Box className="card-body" sx={{ padding: "0" }}>
                    <Grid container spacing={3}>
                      {/* Technology Type */}
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={touched.type && Boolean(errors.type)}
                        >
                          <InputLabel id="technology-type-label">
                            Technology Type *
                          </InputLabel>
                          <Field
                            name="type"
                            as={Select}
                            labelId="technology-type-label"
                            label="Technology Type *"
                            value={values.type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "#3F30FF",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#3F30FF",
                                },
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select Technology Type
                            </MenuItem>
                            {technologyTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="type"
                            component="div"
                            style={{
                              color: "#d32f2f",
                              fontSize: "0.75rem",
                              marginTop: "3px",
                              marginLeft: "14px",
                            }}
                          />
                        </FormControl>
                      </Grid>

                      {/* Technology Name */}
                      <Grid item xs={12} md={6}>
                        <Field
                          name="technology"
                          as={TextField}
                          fullWidth
                          label="Technology Name *"
                          variant="outlined"
                          value={values.technology}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.technology && Boolean(errors.technology)}
                          helperText={touched.technology && errors.technology}
                          placeholder="e.g., React, Node.js, MySQL"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#3F30FF",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#3F30FF",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              "&.Mui-focused": {
                                color: "#3F30FF",
                              },
                            },
                          }}
                        />
                      </Grid>

                      {/* Status Dropdown */}
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={touched.status && Boolean(errors.status)}
                        >
                          <InputLabel id="status-label">
                            Status *
                          </InputLabel>
                          <Field
                            name="status"
                            as={Select}
                            labelId="status-label"
                            label="Status *"
                            value={values.status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "#3F30FF",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#3F30FF",
                                },
                              },
                            }}
                          >
                            <MenuItem value={true}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: '#4CAF50',
                                  }}
                                />
                                Active
                              </Box>
                            </MenuItem>
                            <MenuItem value={false}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: '#f44336',
                                  }}
                                />
                                Inactive
                              </Box>
                            </MenuItem>
                          </Field>
                          <ErrorMessage
                            name="status"
                            component="div"
                            style={{
                              color: "#d32f2f",
                              fontSize: "0.75rem",
                              marginTop: "3px",
                              marginLeft: "14px",
                            }}
                          />
                        </FormControl>
                      </Grid>

                      {/* Submit Button */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "flex-end",
                            mt: 2,
                          }}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() => navigate("/tech-stack/techstacklist")}
                            sx={{
                              borderColor: "#6c757d",
                              color: "#6c757d",
                              "&:hover": {
                                borderColor: "#545b62",
                                backgroundColor: "#f8f9fa",
                              },
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || loading}
                            className="commonBtn"
                            sx={{
                              backgroundColor: "#3F30FF",
                              "&:hover": {
                                backgroundColor: "#2f23cc",
                              },
                              minWidth: "120px",
                            }}
                          >
                            {isSubmitting || loading ? "Updating..." : "Update Tech Stack"}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </div>
      </div>
    </StyledDashboardLayout>
  );
};

export default EditTechStack;